/**
 * WORKSPACE-000001 / 000002 — shared commerce field helpers.
 * Never invents data: missing metrics render COMING SOON with an explicit source class.
 * Cross-nav deep links follow COMMERCE-MODEL-000001.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { commerceDeepLink } from './FounderCommerceShell';

export type FieldSource =
  | 'SP-API'
  | 'MarketSync'
  | 'Derived'
  | 'Calculated'
  | 'Estimated'
  | 'COMING_SOON';

/** Marketplace ID → Amazon retail product URL host (extend as markets unlock). */
const MARKETPLACE_DP_HOST: Record<string, string> = {
  A21TJRUUN4KGV: 'www.amazon.in', // India
  ATVPDKIKX0DER: 'www.amazon.com', // US
  A1F83G8C2ARO7P: 'www.amazon.co.uk', // UK
  A1PA6795UKMFR9: 'www.amazon.de', // DE
  A13V1IB3VIYZZH: 'www.amazon.fr', // FR
  APJ6JRA9NG5V4: 'www.amazon.it', // IT
  A1RKKUPIHCS9HS: 'www.amazon.es', // ES
  A1VC38T7YXB528: 'www.amazon.co.jp', // JP
  A39IBJ37TRP1C6: 'www.amazon.com.au', // AU
  A2Q3Y263D00KWC: 'www.amazon.com.br', // BR
  A2EUQ1WTGCTBG2: 'www.amazon.ca', // CA
};

export function amazonProductUrl(asin: string | undefined | null, marketplaceId?: string | null): string | null {
  const a = (asin || '').trim().toUpperCase();
  if (!a || a.length < 8) return null;
  const mp = (marketplaceId || 'A21TJRUUN4KGV').trim();
  const host = MARKETPLACE_DP_HOST[mp] || 'www.amazon.in';
  return `https://${host}/dp/${encodeURIComponent(a)}`;
}

export function ComingSoonCell({ label = 'NOT AVAILABLE' }: { label?: string }) {
  return (
    <span
      className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600"
      title="Value not available from a declared Amazon/MarketSync source. Never shown as invent 0."
    >
      {label}
    </span>
  );
}

/** Explicit NOT AVAILABLE cell (COMMERCE-ENGINE-000001). Same semantics as ComingSoonCell default. */
export function NotAvailableCell() {
  return <ComingSoonCell label="NOT AVAILABLE" />;
}

export function SourceChip({ source }: { source: FieldSource }) {
  const tone =
    source === 'COMING_SOON'
      ? 'bg-slate-100 text-slate-600'
      : source === 'Calculated' || source === 'Derived'
        ? 'bg-indigo-50 text-indigo-800'
        : source === 'MarketSync'
          ? 'bg-emerald-50 text-emerald-800'
          : source === 'SP-API'
            ? 'bg-sky-50 text-sky-800'
            : 'bg-amber-50 text-amber-900';
  return (
    <span className={`ml-1 rounded px-1 py-0.5 text-[9px] font-medium uppercase tracking-wide ${tone}`}>
      {source === 'COMING_SOON' ? 'soon' : source}
    </span>
  );
}

export function HeaderWithSource({
  title,
  source,
}: {
  title: string;
  source: FieldSource;
}): ReactNode {
  return (
    <span className="inline-flex items-center gap-0.5">
      {title}
      <SourceChip source={source} />
    </span>
  );
}

export function MoneyCell({
  listing,
  shipping,
  currency,
  highlightShipping,
}: {
  listing?: string | null;
  shipping?: string | null;
  currency?: string | null;
  highlightShipping?: boolean;
}) {
  if (!listing && !shipping) {
    return <ComingSoonCell />;
  }
  const shipN = Number(shipping);
  const hasShip = shipping != null && shipping !== '' && !Number.isNaN(shipN) && shipN > 0;
  const title = [
    listing != null && listing !== '' ? `Listing: ${listing}` : null,
    shipping != null && shipping !== '' ? `Shipping: ${shipping}` : null,
    listing != null && shipping != null
      ? `Total: ${Number(listing) + Number(shipping || 0)}`
      : null,
    currency ? `Currency: ${currency}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <span
      className={`font-mono tabular-nums ${hasShip && highlightShipping ? 'rounded bg-amber-100 px-1 text-amber-950' : ''}`}
      title={title || undefined}
    >
      {listing ?? '—'}
      {currency ? <span className="ml-1 text-[10px] text-slate-500">{currency}</span> : null}
      {hasShip ? <span className="ml-1 text-[10px] text-amber-800">+ship</span> : null}
    </span>
  );
}

export function AsinLink({
  asin,
  marketplaceId,
  connectionId,
}: {
  asin?: string | null;
  marketplaceId?: string | null;
  connectionId?: string | null;
}) {
  if (!asin) return <ComingSoonCell />;
  const retail = amazonProductUrl(asin, marketplaceId);
  const products = commerceDeepLink('catalog', {
    connection: connectionId || undefined,
    asin,
    q: asin,
  });
  return (
    <span className="inline-flex items-center gap-1.5 font-mono">
      <Link
        to={products}
        className="text-teal-800 underline-offset-2 hover:underline dark:text-teal-300"
        title="Open in Products workspace"
      >
        {asin}
      </Link>
      {retail ? (
        <a
          href={retail}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] font-medium text-slate-500 hover:text-slate-800"
          title={`Amazon detail page (${marketplaceId || 'mp'})`}
          aria-label={`Open ASIN ${asin} on Amazon`}
        >
          ↗
        </a>
      ) : null}
    </span>
  );
}

/** Platform Products workspace (business SoT for listing identity). */
export function SkuLink({
  sku,
  connectionId,
}: {
  sku?: string | null;
  connectionId?: string | null;
}) {
  if (!sku) return <span className="text-slate-400">—</span>;
  return (
    <Link
      to={commerceDeepLink('catalog', { connection: connectionId || undefined, q: sku })}
      className="font-mono text-teal-800 underline-offset-2 hover:underline dark:text-teal-300"
      title="Open SKU in Products"
    >
      {sku}
    </Link>
  );
}

export function OrderLink({
  orderId,
  connectionId,
}: {
  orderId?: string | null;
  connectionId?: string | null;
}) {
  if (!orderId) return <span className="text-slate-400">—</span>;
  return (
    <Link
      to={commerceDeepLink('orders', { connection: connectionId || undefined, q: orderId })}
      className="font-mono text-teal-800 underline-offset-2 hover:underline dark:text-teal-300"
      title="Open in Orders"
    >
      {orderId}
    </Link>
  );
}

/** Orders ↓ Order Items drill-down: opens the lines belonging to one Amazon order. */
export function OrderItemsLink({
  orderId,
  connectionId,
}: {
  orderId?: string | null;
  connectionId?: string | null;
}) {
  if (!orderId) return <span className="text-slate-400">—</span>;
  return (
    <Link
      to={commerceDeepLink('order_items', {
        connection: connectionId || undefined,
        order_id: orderId,
      })}
      className="inline-flex min-h-8 items-center rounded-md bg-slate-100 px-2 text-[11px] font-medium text-slate-700 transition-colors duration-200 hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
      title={`Open order lines for ${orderId}`}
      aria-label={`Open order lines for order ${orderId}`}
    >
      Lines
    </Link>
  );
}

export function SettlementLink({
  settlementId,
  connectionId,
}: {
  settlementId?: string | null;
  connectionId?: string | null;
}) {
  if (!settlementId) return <span className="text-slate-400">—</span>;
  return (
    <Link
      to={commerceDeepLink('finance', { connection: connectionId || undefined, q: settlementId })}
      className="font-mono text-teal-800 underline-offset-2 hover:underline dark:text-teal-300"
      title="Open settlement in Finance"
    >
      {settlementId}
    </Link>
  );
}

export function ShipmentLink({ shipmentId }: { shipmentId?: string | null }) {
  if (!shipmentId) return <span className="text-slate-400">—</span>;
  return (
    <Link
      to={`/amazon/shipments?q=${encodeURIComponent(shipmentId)}`}
      className="font-mono text-slate-600 underline-offset-2 hover:underline"
      title="Shipments reserved workspace"
    >
      {shipmentId}
    </Link>
  );
}

export function WorkspaceLegend() {
  return (
    <p className="text-[11px] text-slate-500">
      Sources:{' '}
      <span className="font-medium text-sky-800">SP-API</span> Amazon response ·{' '}
      <span className="font-medium text-emerald-800">MarketSync</span> canonical PG ·{' '}
      <span className="font-medium text-indigo-800">Calculated/Derived</span> Logic Bible formula ·{' '}
      <span className="font-medium text-slate-600">NOT AVAILABLE</span> no Discovery-backed value (never invent).
    </p>
  );
}
